import { apiFetch, readErrorMessage, readJson, readJsonArray } from '../api/client';
import {
  PATIENT_TRACKING_API_BASE,
  type AlertingHistoryDto,
  type AlertingHistoryUpsertBody,
  type CoveredEntityDto,
  type CoveredEntityUpsertBody,
  type OurPharmacyDto,
  type OurPharmacyUpsertBody,
  type PatientDto,
  type PatientDuplicateMatch,
  type PatientSaveResult,
  type PatientUpsertBody,
  type TrackerEntryDto,
  type TrackerEntryUpsertBody
} from './patientTrackingTypes';

/**
 * Thrown when a Patient create/update is rejected by the server because possible duplicates exist
 * (status 409). The caller (typically the form popup) can show "use existing / save anyway".
 */
export class PatientDuplicateError extends Error {
  constructor(message: string, public readonly possibleDuplicates: PatientDuplicateMatch[]) {
    super(message);
    this.name = 'PatientDuplicateError';
  }
}

async function ok<T>(res: Response): Promise<T> {
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<T>(res);
}

async function okArray<T>(res: Response): Promise<T[]> {
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<T>(res);
}

async function okVoid(res: Response): Promise<void> {
  if (!res.ok) throw new Error(await readErrorMessage(res));
}

async function patientSaveOk(res: Response): Promise<PatientSaveResult> {
  if (res.status === 409) {
    type ConflictBody = { errorText?: string; possibleDuplicates?: PatientDuplicateMatch[] };
    let body: ConflictBody = {};
    try {
      body = (await res.json()) as ConflictBody;
    } catch {
      // ignore - empty body
    }
    throw new PatientDuplicateError(body.errorText ?? 'Possible duplicate patient(s).', body.possibleDuplicates ?? []);
  }
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<PatientSaveResult>(res);
}

export type PatientTrackingApi = ReturnType<typeof createPatientTrackingApi>;

export function createPatientTrackingApi(base = PATIENT_TRACKING_API_BASE) {
  return {
    // Our pharmacies
    listPharmacies: () => apiFetch(`${base}/pharmacies`).then(okArray<OurPharmacyDto>),
    createPharmacy: (body: OurPharmacyUpsertBody) =>
      apiFetch(`${base}/pharmacies`, {
        method: 'POST',
        body: JSON.stringify(body)
      }).then(ok<OurPharmacyDto>),
    updatePharmacy: (id: string, body: OurPharmacyUpsertBody) =>
      apiFetch(`${base}/pharmacies/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      }).then(ok<OurPharmacyDto>),
    deletePharmacy: (id: string) =>
      apiFetch(`${base}/pharmacies/${id}`, { method: 'DELETE' }).then(okVoid),

    // Patients (master)
    listPatients: () => apiFetch(`${base}/patients`).then(okArray<PatientDto>),
    createPatient: (body: PatientUpsertBody) =>
      apiFetch(`${base}/patients`, {
        method: 'POST',
        body: JSON.stringify(body)
      }).then(patientSaveOk),
    updatePatient: (id: string, body: PatientUpsertBody) =>
      apiFetch(`${base}/patients/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      }).then(patientSaveOk),
    deletePatient: (id: string) =>
      apiFetch(`${base}/patients/${id}`, { method: 'DELETE' }).then(okVoid),

    // Tracker entries (patient communication issues)
    listTrackerEntries: () => apiFetch(`${base}/tracker`).then(okArray<TrackerEntryDto>),
    createTrackerEntry: (body: TrackerEntryUpsertBody) =>
      apiFetch(`${base}/tracker`, {
        method: 'POST',
        body: JSON.stringify(body)
      }).then(ok<TrackerEntryDto>),
    updateTrackerEntry: (id: string, body: TrackerEntryUpsertBody) =>
      apiFetch(`${base}/tracker/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      }).then(ok<TrackerEntryDto>),
    deleteTrackerEntry: (id: string) =>
      apiFetch(`${base}/tracker/${id}`, { method: 'DELETE' }).then(okVoid),

    // Covered entities
    listCoveredEntities: () =>
      apiFetch(`${base}/covered-entities`).then(okArray<CoveredEntityDto>),
    createCoveredEntity: (body: CoveredEntityUpsertBody) =>
      apiFetch(`${base}/covered-entities`, {
        method: 'POST',
        body: JSON.stringify(body)
      }).then(ok<CoveredEntityDto>),
    updateCoveredEntity: (id: string, body: CoveredEntityUpsertBody) =>
      apiFetch(`${base}/covered-entities/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      }).then(ok<CoveredEntityDto>),
    deleteCoveredEntity: (id: string) =>
      apiFetch(`${base}/covered-entities/${id}`, { method: 'DELETE' }).then(okVoid),

    // Alerting history
    listAlertingHistory: () =>
      apiFetch(`${base}/alerting-history`).then(okArray<AlertingHistoryDto>),
    createAlertingHistory: (body: AlertingHistoryUpsertBody) =>
      apiFetch(`${base}/alerting-history`, {
        method: 'POST',
        body: JSON.stringify(body)
      }).then(ok<AlertingHistoryDto>),
    updateAlertingHistory: (id: string, body: AlertingHistoryUpsertBody) =>
      apiFetch(`${base}/alerting-history/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      }).then(ok<AlertingHistoryDto>),
    deleteAlertingHistory: (id: string) =>
      apiFetch(`${base}/alerting-history/${id}`, { method: 'DELETE' }).then(okVoid)
  };
}

export const patientTrackingApi = createPatientTrackingApi();
